(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.QCatalogSearchUI = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function mount(root, catalog) {
    if (!root || !catalog || typeof catalog.subscribe !== 'function') {
      throw new TypeError('QCatalogSearchUI.mount requires a section root and catalog');
    }

    const button = root.querySelector('[data-catalog-search-boost]');
    const status = root.querySelector('[data-catalog-search-status]');
    const tooltip = root.querySelector('[data-catalog-search-tooltip]');
    const readyMark = root.querySelector('[data-catalog-search-ready-mark]');
    if (!button || !status || !tooltip) return { destroy() {} };

    let destroyed = false;
    let hovered = false;
    let focused = false;
    let touchTimer = null;
    let hoverCloseTimer = null;
    let touchOpen = false;
    let dismissedUntilExit = false;
    let unsubscribe = null;
    let windowPointerType = '';
    let latestState = null;

    const setTooltip = (open) => {
      tooltip.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    };

    const updateTooltip = () => {
      if (!dismissedUntilExit && (hovered || focused || touchOpen)) setTooltip(true);
      else setTooltip(false);
    };

    const clearTouchTimer = () => {
      if (touchTimer !== null) {
        clearTimeout(touchTimer);
        touchTimer = null;
      }
    };

    const clearHoverCloseTimer = () => {
      if (hoverCloseTimer !== null) {
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = null;
      }
    };

    const render = (state) => {
      if (destroyed || !state) return;
      latestState = state;
      const phase = state.phase || 'idle';
      const enabled = !!state.enabled;
      const boostPhase = enabled ? phase : 'idle';
      const persistenceUnavailable = state.persistence === 'unavailable';
      let statusText = '';
      let displayPhase = boostPhase;

      if (boostPhase === 'error') {
        statusText = button.dataset.labelError || 'Vorbereitung unterbrochen – erneut versuchen.';
      } else if (boostPhase === 'ready') {
        statusText = button.dataset.labelReady || 'Suche bereit';
        if (persistenceUnavailable) {
          statusText += ` · ${button.dataset.labelLocalOnly || 'Nur auf dieser Seite verfügbar'}`;
        }
      } else if (boostPhase === 'loading' || boostPhase === 'restoring') {
        statusText = (button.dataset.labelLoading || 'Suchdaten werden geladen … [loaded] von [total] Karten vorbereitet')
          .replace(/\[loaded\]/g, String(state.loaded ?? 0))
          .replace(/\[total\]/g, state.total == null ? '…' : String(state.total));
        if (persistenceUnavailable) {
          statusText += ` · ${button.dataset.labelLocalOnly || 'Nur auf dieser Seite verfügbar'}`;
        }
      } else if (persistenceUnavailable) {
        statusText = button.dataset.labelLocalOnly || 'Nur auf dieser Seite verfügbar';
      }

      button.dataset.phase = displayPhase;
      const actionInProgress = enabled && (phase === 'loading' || phase === 'restoring' || phase === 'ready');
      button.setAttribute('aria-disabled', String(actionInProgress));
      button.setAttribute('aria-busy', String(boostPhase === 'loading' || boostPhase === 'restoring'));
      button.setAttribute('aria-label', button.dataset.labelIdle || 'Suchdaten vorladen');
      if (readyMark) readyMark.hidden = boostPhase !== 'ready';
      status.textContent = statusText;
      status.dataset.phase = displayPhase;
    };

    const onPointerEnter = (event) => {
      if (event.pointerType === 'touch') return;
      clearHoverCloseTimer();
      hovered = true;
      dismissedUntilExit = false;
      updateTooltip();
    };
    const onPointerLeave = (event) => {
      if (event.pointerType === 'touch') return;
      clearHoverCloseTimer();
      hoverCloseTimer = setTimeout(() => {
        hoverCloseTimer = null;
        hovered = false;
        if (!focused) dismissedUntilExit = false;
        updateTooltip();
      }, 120);
    };
    const onFocus = () => {
      focused = true;
      dismissedUntilExit = false;
      updateTooltip();
    };
    const onBlur = () => {
      focused = false;
      if (!hovered) dismissedUntilExit = false;
      updateTooltip();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !tooltip.hidden) {
        event.preventDefault();
        dismissedUntilExit = true;
        touchOpen = false;
        clearTouchTimer();
        setTooltip(false);
      }
    };
    const onClick = (event) => {
      const touchActivation = event.detail !== 0 && windowPointerType === 'touch';
      windowPointerType = '';
      if (touchActivation) {
        focused = false;
        touchOpen = true;
        dismissedUntilExit = false;
        updateTooltip();
        clearTouchTimer();
        touchTimer = setTimeout(() => {
          touchTimer = null;
          touchOpen = false;
          updateTooltip();
        }, 3000);
      }

      const currentState = typeof catalog.getState === 'function' ? catalog.getState() : latestState;
      const alreadyActive = currentState?.enabled && ['loading', 'restoring', 'ready'].includes(currentState.phase);
      if (alreadyActive || button.getAttribute('aria-disabled') === 'true') return;
      const action = currentState?.enabled && currentState.phase === 'error'
        ? catalog.retry
        : catalog.enable;
      if (typeof action === 'function') Promise.resolve().then(() => action.call(catalog)).catch(() => {});
    };
    const onPointerDown = (event) => { windowPointerType = event.pointerType || ''; };
    const onPointerCancel = () => { windowPointerType = ''; };
    const onTooltipPointerEnter = (event) => {
      if (event.pointerType === 'touch') return;
      clearHoverCloseTimer();
      hovered = true;
      updateTooltip();
    };
    const onTooltipPointerLeave = (event) => {
      if (event.pointerType === 'touch') return;
      hovered = false;
      if (!focused) dismissedUntilExit = false;
      updateTooltip();
    };

    button.addEventListener('pointerdown', onPointerDown);
    button.addEventListener('pointercancel', onPointerCancel);
    button.addEventListener('pointerenter', onPointerEnter);
    button.addEventListener('pointerleave', onPointerLeave);
    button.addEventListener('focus', onFocus);
    button.addEventListener('blur', onBlur);
    button.addEventListener('keydown', onKeyDown);
    button.addEventListener('click', onClick);
    tooltip.addEventListener('pointerenter', onTooltipPointerEnter);
    tooltip.addEventListener('pointerleave', onTooltipPointerLeave);
    unsubscribe = catalog.subscribe(render);

    return {
      destroy() {
        if (destroyed) return;
        destroyed = true;
        clearTouchTimer();
        clearHoverCloseTimer();
        if (typeof unsubscribe === 'function') unsubscribe();
        button.removeEventListener('pointerdown', onPointerDown);
        button.removeEventListener('pointercancel', onPointerCancel);
        button.removeEventListener('pointerenter', onPointerEnter);
        button.removeEventListener('pointerleave', onPointerLeave);
        button.removeEventListener('focus', onFocus);
        button.removeEventListener('blur', onBlur);
        button.removeEventListener('keydown', onKeyDown);
        button.removeEventListener('click', onClick);
        tooltip.removeEventListener('pointerenter', onTooltipPointerEnter);
        tooltip.removeEventListener('pointerleave', onTooltipPointerLeave);
      }
    };
  }

  return { mount };
});
